import { useMutation, useQueryClient } from '@tanstack/react-query';
import produce from 'immer';

import useToastMsg from '../../common/useToastMsg';
import { PRODUCT } from '../../../constants/queryKeys';
import productToastMsg from '../../../constants/service/toastMsg/productToastMsg';
import ProductApi from '../../../lib/api/product/ProductApi';
import { COMMON_RES_CODE } from '../../../constants/responseCode/commonResCode';
import { setRequestForm } from '../../../lib/common';
import { CONFIRM_PROPERTY_LIST_TYPE } from '../../../constants/service/app/tagConstants';
import { extractErrorMessage } from '../../../lib/utils/checkErrorResponse';

const useConfirmPropertyMutate = (setType) => {
  const queryClient = useQueryClient();
  const toastMsg = useToastMsg();

  const confirmProperty = useMutation({
    mutationFn: async (payload) => {
      const result = await ProductApi.permitRegistrationProductProperty(setRequestForm(payload));

      if (result?.status !== COMMON_RES_CODE.SUCCESS) {
        throw new Error(result.msg);
      }

      queryClient.setQueryData([...PRODUCT.PRODUCT_DETAIL], (prev) => {
        return produce(prev, (draft) => {
          draft.productDetail.productUpdateLogId = result.data.productUpdateLogId;
          draft.productDetail.proInfo.productPropertyList[payload.productPropertyType].list.active =
            draft.productDetail.proInfo.productPropertyList[payload.productPropertyType].list.confirmReady;
          draft.productDetail.proInfo.productPropertyList[payload.productPropertyType].list.confirmReady = null;
        });
      });
    },
    onSuccess: () => {
      toastMsg.success(productToastMsg.PERMIT_REGISTRATION_PRODUCT_PROPERTY_REQUEST_SUCCESS);
      setType(CONFIRM_PROPERTY_LIST_TYPE.ACTIVE);
    },
    onError: (e) => {
      toastMsg.error(extractErrorMessage(e, productToastMsg.PERMIT_REGISTRATION_PRODUCT_PROPERTY_REQUEST_ERROR));
    },
  });

  const rejectProperty = useMutation({
    mutationFn: async (payload) => {
      const result = await ProductApi.rejectRegistrationProductProperty(setRequestForm(payload));

      if (result?.status !== COMMON_RES_CODE.SUCCESS) {
        throw new Error(result.msg);
      }

      queryClient.setQueryData([...PRODUCT.PRODUCT_DETAIL], (prev) => {
        return produce(prev, (draft) => {
          draft.productDetail.proInfo.productPropertyList[payload.productPropertyType].rejectInfo = {
            type: payload.type,
            reason: payload.reason,
          };
          draft.productDetail.proInfo.productPropertyList[payload.productPropertyType].list.reject =
            draft.productDetail.proInfo.productPropertyList[payload.productPropertyType].list.confirmReady;
          draft.productDetail.proInfo.productPropertyList[payload.productPropertyType].list.confirmReady = null;
        });
      });
    },
    onSuccess: () => {
      toastMsg.success(productToastMsg.REJECT_REGISTRATION_PRODUCT_PROPERTY_REQUEST_SUCCESS);
      setType(CONFIRM_PROPERTY_LIST_TYPE.REJECT);
    },
    onError: (e) => {
      toastMsg.error(extractErrorMessage(e, productToastMsg.REJECT_REGISTRATION_PRODUCT_PROPERTY_REQUEST_ERROR));
    },
  });

  return {
    confirmProperty,
    rejectProperty,
  };
};

export default useConfirmPropertyMutate;
